// Inline OAuth2 / PKCE flow against Gatehouse.
//
// We deliberately keep this inline rather than pulling in
// @aquifer-cloud/aquifer-ui's OAuthAuthProvider — the existing
// AuthProvider/AccountProvider tree is invasive to replace, and Blanche
// already accepts Gatehouse-issued JWTs alongside the legacy session
// cookie (Blanche PR #103). Storing the access_token under the same
// `dev_token` localStorage key the Axios interceptor already reads
// (data/api.ts:24) is the smallest change that lets the existing app
// run signed in via Gatehouse.
//
// Once we want to retire the cookie path entirely, swap this for the
// shared aquifer-ui provider so all consumer SPAs use the same flow.

const STATE_KEY = 'gatehouse_oauth_state';
const VERIFIER_KEY = 'gatehouse_oauth_verifier';
const REDIRECT_PATH_KEY = 'gatehouse_oauth_redirect';
const TOKEN_KEY = 'dev_token'; // matches the key data/api.ts reads from
const REFRESH_KEY = 'gatehouse_refresh_token';

// Configuration is environment-driven; both vars must be set together.
// When either is missing the integration is disabled — the login button
// hides itself and direct navigation to /auth/callback errors out
// rather than silently bouncing prod traffic to the dev IdP.
const GATEHOUSE_ORIGIN = process.env['NX_PUBLIC_GATEHOUSE_ORIGIN'] || '';
const CLIENT_ID = process.env['NX_PUBLIC_GATEHOUSE_CLIENT_ID'] || '';
const SCOPE = 'openid profile email';

/** True iff Gatehouse OAuth is wired up in this build. */
export function isGatehouseEnabled(): boolean {
  return Boolean(GATEHOUSE_ORIGIN && CLIENT_ID);
}

function redirectUri(): string {
  // The SPA is mounted at /integrations (see app/app.js basename), and
  // /integrations/auth/callback is registered with Gatehouse as one of
  // the allowed redirect URIs.
  return `${window.location.origin}/integrations/auth/callback`;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(byteLen = 32): string {
  const bytes = new Uint8Array(byteLen);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256Base64Url(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/** Kick off the auth flow: stash PKCE state, redirect to Gatehouse. */
export async function initiateGatehouseLogin(redirectAfter = '/'): Promise<void> {
  if (!isGatehouseEnabled()) {
    throw new Error('Gatehouse OAuth is not configured for this environment');
  }
  const verifier = randomString(48);
  const challenge = await sha256Base64Url(verifier);
  const state = randomString(16);

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(REDIRECT_PATH_KEY, redirectAfter);

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    redirect_uri: redirectUri(),
    response_type: 'code',
    scope: SCOPE,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  window.location.assign(`${GATEHOUSE_ORIGIN}/api/v1/oauth/authorize?${params}`);
}

export interface CallbackResult {
  ok: boolean;
  /** Where to navigate the user after a successful exchange. */
  redirectTo: string;
  /** Human-readable error if the flow couldn't complete. */
  error?: string;
}

interface TokenResponse {
  access_token?: string;
  refresh_token?: string;
}

function persistTokens(payload: TokenResponse): void {
  if (payload.access_token) localStorage.setItem(TOKEN_KEY, payload.access_token);
  if (payload.refresh_token) localStorage.setItem(REFRESH_KEY, payload.refresh_token);
}

async function postToken(body: URLSearchParams): Promise<TokenResponse | null> {
  let resp: Response;
  try {
    resp = await fetch(`${GATEHOUSE_ORIGIN}/api/v1/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  } catch {
    return null;
  }
  if (!resp.ok) return null;
  try {
    return (await resp.json()) as TokenResponse;
  } catch {
    return null;
  }
}

/**
 * Complete the OAuth callback: validate state, exchange code for tokens,
 * persist access_token + refresh_token, return where to navigate next.
 */
export async function completeGatehouseCallback(
  search: string,
): Promise<CallbackResult> {
  if (!isGatehouseEnabled()) {
    return { ok: false, redirectTo: '/login', error: 'Gatehouse OAuth is not configured' };
  }

  const params = new URLSearchParams(search);
  const code = params.get('code');
  const returnedState = params.get('state');
  const errParam = params.get('error');

  const storedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const redirectTo = sessionStorage.getItem(REDIRECT_PATH_KEY) || '/';

  // Always clear single-use state once we've read it, so a refresh of
  // the callback page can't replay anything.
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(REDIRECT_PATH_KEY);

  if (errParam) {
    return { ok: false, redirectTo: '/login', error: `Gatehouse returned: ${errParam}` };
  }
  if (!code || !returnedState || !verifier) {
    return { ok: false, redirectTo: '/login', error: 'Missing code/state/verifier in callback' };
  }
  if (returnedState !== storedState) {
    return { ok: false, redirectTo: '/login', error: 'OAuth state mismatch' };
  }

  const payload = await postToken(new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri(),
    client_id: CLIENT_ID,
    code_verifier: verifier,
  }));
  if (!payload || !payload.access_token) {
    return { ok: false, redirectTo: '/login', error: 'Token exchange failed' };
  }

  persistTokens(payload);
  return { ok: true, redirectTo };
}

/**
 * Trade the stored refresh token for a fresh access token. Returns the
 * new access token on success; on any failure clears local tokens (the
 * caller can then redirect to /login). Concurrent calls are coalesced
 * via an in-flight promise so a burst of 401s only refreshes once.
 */
let inFlightRefresh: Promise<string | null> | null = null;
export function refreshGatehouseToken(): Promise<string | null> {
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = (async () => {
    try {
      if (!isGatehouseEnabled()) return null;
      const refresh = localStorage.getItem(REFRESH_KEY);
      if (!refresh) return null;

      const payload = await postToken(new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refresh,
        client_id: CLIENT_ID,
      }));
      if (!payload || !payload.access_token) {
        // Refresh failed — wipe so the next 401 doesn't loop here.
        clearGatehouseSession();
        return null;
      }
      persistTokens(payload);
      return payload.access_token;
    } finally {
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
}

/** Drop the locally-cached Gatehouse tokens. */
export function clearGatehouseSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}
